import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, Input, HostListener, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IonicModule } from '@ionic/angular';
import { validate as validateUuid } from 'uuid';

import { downloadSlackAttachment } from 'src/app/utils/utils';
import { ApiService } from 'src/services/api.service';

@Component({
  standalone: true,
  imports: [CommonModule, IonicModule],
  selector: 'app-inline-file-attachment',
  templateUrl: './inline-file-attachment.component.html',
  styleUrls: ['./inline-file-attachment.component.scss'],
})
export class InlineFileAttachmentComponent implements OnInit, OnDestroy {
  @Input() id?: string;
  @Input() teamId: string;
  @Input() size: number = 160;
  @Input() file?: File;
  @Input() mimetype?: string;
  @Input() privateUrl?: string;
  @Input() onError: () => void;
  @Input() conversationId?: string;
  @Input() attachments?: any[];
  @Input() attachmentsIdKey?: string;
  @Input() attachmentsIndex?: number;

  cachedUrlsById: Record<string, any> = {};

  isLoading = true;
  name?: string;
  shortname?: string;
  url?: string;
  width?: number;
  height?: number;
  error?: any;
  previewExpanded = false;
  previewLoading = false;
  previewUrl?: SafeResourceUrl;
  previewId?: string;
  previewName?: string;
  previewMimetype?: string;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
  ) {}

  async ngOnInit() {
    this.isLoading = true;

    if (this.id) {
      await this.loadSlackFileDetails();
    } else if (this.file) {
      this.name = this.file.name;
      this.shortname = this.name?.length > 30 ? this.name.substring(0, 20) + '...' : this.name;
      this.mimetype = this.file.type;

      let reader = new FileReader();

      reader.readAsDataURL(this.file);

      reader.onloadend = () => {
        this.url = reader.result.toString();
      };
    }

    this.isLoading = false;
    this.cdr.detectChanges();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (!this.attachments?.length || this.attachments.length <= 1 || !this.previewExpanded) {
      return;
    }

    if (event.key === 'ArrowRight') {
      this.attachmentsIndex++;

      if (this.attachmentsIndex >= this.attachments.length) {
        this.attachmentsIndex = 0;
      }
    } else if (event.key === 'ArrowLeft') {
      this.attachmentsIndex--;

      if (this.attachmentsIndex < 0) {
        this.attachmentsIndex = this.attachments.length - 1;
      }
    }

    this.expandPreviewImageForId(this.attachments[this.attachmentsIndex][this.attachmentsIdKey]);
  }

  async loadSlackFileDetails() {
    try {
      const response: any = validateUuid(this.id)
        ? await this.apiService.download(
            `/conversations/${this.conversationId}/files/${this.id}/preview?size=${this.size}`,
            'GET',
          )
        : await this.apiService.download(
            `/slack/files/${this.id}/thumb?thumbSize=${this.size}&teamId=${this.teamId}`,
            'GET',
          );
      const contentDisposition: string = response.headers.get('content-disposition');
      const dimensionsHeader = response.headers.get('x-content-dimensions');
      const contentLength = response.headers.get('content-length');

      // Use the original content type if available (for videos/PDFs), otherwise use the preview content type
      this.mimetype = response.headers.get('x-original-content-type') || response.headers.get('content-type');
      this.name =
        contentDisposition
          .split(';')
          .find((p) => p.startsWith('filename'))
          ?.split('=')[1] ?? 'file.txt';
      this.shortname = this.name?.length > 30 ? this.name.substring(0, 20) + '...' : this.name;

      const [originalWidth, originalHeight] = dimensionsHeader?.split('x') ?? [];

      if (originalHeight && originalWidth) {
        this.width = originalWidth > originalHeight ? this.size : this.size * (originalWidth / originalHeight);
        this.height = originalWidth > originalHeight ? this.size * (originalHeight / originalWidth) : this.size;
      }

      if (!Number(contentLength)) {
        return;
      }

      let reader = new FileReader();

      reader.readAsDataURL(response.body);

      return new Promise<void>((resolve) => {
        reader.onloadend = () => {
          const url = reader.result.toString();
          this.url = url;
          resolve();
        };
      });
    } catch (e: any) {
      this.error = e;
    }
  }

  shouldShowPreview(): boolean {
    // Show preview if we have a thumbnail URL, or if it's a PDF/video (even without thumbnail)
    return !!this.url || this.mimetype === 'application/pdf' || this.mimetype?.startsWith('video/');
  }

  downloadFile(id: string) {
    if (!id) {
      return;
    }

    downloadSlackAttachment(this.apiService, id, this.teamId, this.conversationId);
  }

  async expandPreviewImageForId(id: string) {
    if (!id) {
      return;
    }

    this.previewExpanded = true;
    this.previewName = this.attachments[this.attachmentsIndex].name;
    this.previewId = id;

    if (this.cachedUrlsById[id]) {
      this.previewUrl = this.cachedUrlsById[id].url;
      this.previewMimetype = this.cachedUrlsById[id].mimetype;
      this.previewLoading = false;
      return;
    }

    this.previewLoading = true;

    // Use /download endpoint to get the actual file, not the preview image
    const response: any = validateUuid(id)
      ? await this.apiService.download(`/conversations/${this.conversationId}/files/${id}/download`, 'GET')
      : await this.apiService.download(`/slack/files/${id}/download?teamId=${this.teamId}`, 'GET');

    // Get the actual file's content type from the download
    this.previewMimetype = response.headers.get('content-type');

    // For images, PDFs, and videos, create a sanitized URL for preview
    if (
      this.previewMimetype?.startsWith('image/') ||
      this.previewMimetype === 'application/pdf' ||
      this.previewMimetype?.startsWith('video/')
    ) {
      // response.body is already a Blob with the correct type, use it directly
      const blobUrl = URL.createObjectURL(response.body);

      this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
      this.previewLoading = false;

      this.cachedUrlsById[id] = { url: this.previewUrl, mimetype: this.previewMimetype };
      this.cdr.detectChanges();
    } else {
      // For other file types, just set the mimetype - we'll show download button
      this.cachedUrlsById[id] = { mimetype: this.previewMimetype };
      this.previewLoading = false;
      this.cdr.detectChanges();
    }
  }

  onDismissPreview() {
    this.previewExpanded = false;
  }

  onPdfError(event: any) {
    console.error('PDF iframe failed to load:', {
      fileId: this.previewId,
      fileName: this.previewName,
      mimetype: this.previewMimetype,
      event,
    });
  }

  onVideoError(event: any) {
    console.error('Video failed to load:', {
      fileId: this.previewId,
      fileName: this.previewName,
      mimetype: this.previewMimetype,
      event,
    });
  }

  ngOnDestroy() {
    // Clean up blob URLs to prevent memory leaks
    Object.values(this.cachedUrlsById).forEach((cached) => {
      if (cached && typeof cached === 'object' && 'url' in cached) {
        const urlStr = cached.url?.toString();
        if (urlStr && urlStr.startsWith('blob:')) {
          URL.revokeObjectURL(urlStr);
        }
      }
    });
  }
}
